import { useEffect, useRef, useState } from "react"

import I from "./icons"

// Rings for the same window the inbound dial uses, then reports itself missed. A dial that
// outlasts the toast would keep ringing after the rep had been told the call was gone, so the
// inbound TwiML uses this number too — change both together.
const CB_RING = 20

function cbClock(s) {
  return `0:${String(Math.max(0, s)).padStart(2, "0")}`
}

// "18 minutes ago" reads better than a timestamp on a card the rep has seconds to act on.
function cbAgo(ms) {
  const mins = Math.floor((Date.now() - ms) / 60000)
  if (mins < 1) return "just now"
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`
  const days = Math.floor(hours / 24)
  return `${days} day${days === 1 ? "" : "s"} ago`
}

function cbInit(name) {
  return (name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
}

// Top-right notification when someone the rep already dialled calls back while the tab is
// open. Answer / Voicemail / Decline, per the design.
export default function CallbackToast({
  caller,
  inSession,
  midCall,
  onAnswer,
  onVoicemail,
  onDecline,
  onMissed
}) {
  const [left, setLeft] = useState(CB_RING)
  const done = useRef(false)
  const bar = useRef(null)

  // The bar is driven every frame off the same clock the countdown reads, rather than off the
  // one-second tick, which is what made it fall away a chunk at a time. Written straight to the
  // node so a redraw of the card is not needed to move it, and derived from elapsed time rather
  // than accumulated frames so it stays honest if the tab is put aside and comes back.
  useEffect(() => {
    const startedAt = Date.now()
    const total = CB_RING * 1000
    let frame = 0

    const paint = () => {
      const remaining = Math.max(0, total - (Date.now() - startedAt))
      if (bar.current) bar.current.style.width = `${(remaining / total) * 100}%`
      if (remaining > 0) frame = requestAnimationFrame(paint)
    }

    frame = requestAnimationFrame(paint)
    return () => cancelAnimationFrame(frame)
  }, [])

  useEffect(() => {
    const t = setInterval(() => {
      setLeft((v) => {
        if (v <= 1) {
          clearInterval(t)
          if (!done.current) {
            done.current = true
            ;(onMissed || onDecline || (() => {}))("noanswer")
          }
          return 0
        }
        return v - 1
      })
    }, 1000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fire = (fn, how) => {
    done.current = true
    ;(fn || (() => {}))(how)
  }

  if (!caller) return null

  return (
    <div
      className="cbt"
      role="alertdialog"
      aria-label={"Incoming callback from " + (caller.name || caller.number)}
    >
      <div className="cbt-bar">
        <i ref={bar} style={{ width: "100%" }} />
      </div>
      <div className="cbt-top">
        <span className="cbt-live">
          <span className="cbt-dot" />
          Incoming callback
        </span>
        <span className="cbt-timer">{cbClock(left)}</span>
        <button
          type="button"
          className="cbt-x"
          aria-label="Dismiss"
          onClick={() => fire(onMissed || onDecline, "dismissed")}
        >
          {I.close({ size: 14 })}
        </button>
      </div>
      <div className="cbt-who">
        {/* A callback can arrive as a bare number, which initials cannot be taken from and
            which does not want printing twice. */}
        <div className="cbt-av">
          {caller.name ? cbInit(caller.name) : I.phone({ size: 16 })}
        </div>
        <div>
          <div className="cbt-name">{caller.name || caller.number}</div>
          {caller.title ? <div className="cbt-role">{caller.title}</div> : null}
          {caller.name ? <div className="cbt-num">{caller.number}</div> : null}
        </div>
      </div>
      {caller.lastCalledAt ? (
        <div className="cbt-ctx">
          {I.reply({ size: 13 })} You called them {cbAgo(caller.lastCalledAt)}
        </div>
      ) : null}
      {midCall ? (
        <div className="cbt-note">
          You are on a call, so this is not ringing. Answering will end the call
          you are on.
        </div>
      ) : inSession ? (
        <div className="cbt-note">
          Answering pauses your power dialer session — the queue resumes when
          you hang up.
        </div>
      ) : null}
      <div className="cbt-acts">
        <button
          type="button"
          className="cbt-btn answer"
          onClick={() => fire(onAnswer, "answered")}
        >
          {I.phone({ size: 14 })} Answer
        </button>
        <div className="r2">
          <button
            type="button"
            className="cbt-btn voicemail"
            onClick={() => fire(onVoicemail, "voicemail")}
          >
            {I.mic({ size: 13 })} Voicemail
          </button>
          <button
            type="button"
            className="cbt-btn decline"
            onClick={() => fire(onDecline, "declined")}
          >
            {I.close({ size: 13 })} Decline
          </button>
        </div>
      </div>
    </div>
  )
}
